import { __ } from "@wordpress/i18n";
import { useState, useEffect, useRef } from "@wordpress/element";
import AsyncSelect from "react-select/async";
import Description from "./common/Description";
import { reactSelectStylesAjax } from "../../others/helperFunctions";

/**
 * MultiSelect
 * Reusable multi/select component for static options
 *
 * Props:
 * - label, description, placeholder
 * - value (array of values for multi, or single value)
 * - onChange(name, value) -> value is array (multi) or single value
 * - name
 * - options: array of options { value, label }
 * - formatOptionLabel
 * - isMulti: true for multi-select, false for single select
 */
const MultiSelect = ({
    label,
    description,
    placeholder = __(
        "Search and select...",
        "wpbean-quantity-controls-for-woocommerce",
    ),
    value = [],
    onChange,
    name = "multi_select",
    options = [],
    formatOptionLabel,
    isMulti = true,
    className = "",
    containerClass = "",
    width,
    ...props
}) => {
    const [selectedItems, setSelectedItems] = useState([]);
    const prevValueRef = useRef();

    useEffect(() => {
        const currentValue = JSON.stringify(value);
        const prevValue = JSON.stringify(prevValueRef.current);

        if (currentValue !== prevValue) {
            prevValueRef.current = value;

            if (isMulti) {
                if (value && value.length > 0) {
                    const map = {};
                    options.forEach((o) => (map[o.value] = o));
                    const ordered = value.map((id) => map[id]).filter(Boolean);
                    setSelectedItems(ordered);
                } else {
                    setSelectedItems([]);
                }
            } else {
                if (value) {
                    const selected = options.find((o) => o.value === value);
                    setSelectedItems(selected ? [selected] : []);
                } else {
                    setSelectedItems([]);
                }
            }
        }
    }, [value, isMulti, options]);

    const loadItems = (inputValue) => {
        const q = (inputValue || "").toLowerCase();
        return Promise.resolve(
            options.filter((o) => o.label.toLowerCase().includes(q)),
        );
    };

    const handleChange = (selected) => {
        const selectedArray = isMulti
            ? selected || []
            : selected
            ? [selected]
            : [];

        setSelectedItems(selectedArray);

        if (isMulti) {
            const ids = selectedArray.map((s) => s.value);
            onChange?.(name, ids);
        } else {
            onChange?.(name, selectedArray[0] ? selectedArray[0].value : "");
        }
    };

    return (
        <div className={containerClass}>
            {label && (
                <div className="flex items-center gap-2 mb-2">
                    <h3 className="block text-sm font-medium text-gray-700">
                        {label}
                    </h3>
                </div>
            )}

            <div className="relative">
                <AsyncSelect
                    isMulti={isMulti}
                    cacheOptions
                    defaultOptions={options}
                    value={isMulti ? selectedItems : selectedItems[0] || null}
                    onChange={handleChange}
                    loadOptions={loadItems}
                    placeholder={placeholder}
                    styles={reactSelectStylesAjax({ width, isMulti })}
                    noOptionsMessage={({ inputValue }) =>
                        inputValue
                            ? `${__(
                                  "No results found for",
                                  "wpbean-quantity-controls-for-woocommerce",
                              )} "${inputValue}"`
                            : __(
                                  "No options available",
                                  "wpbean-quantity-controls-for-woocommerce",
                              )
                    }
                    formatOptionLabel={formatOptionLabel}
                    isClearable={!isMulti}
                    closeMenuOnSelect={!isMulti}
                    blurInputOnSelect={!isMulti}
                    className={`react-select-container max-wp:!w-auto ${className}`}
                    classNamePrefix="react-select"
                    {...props}
                />
            </div>

            <Description description={description} />

            {selectedItems.length > 0 && (
                <p className="mt-2 text-xs text-blue-600 font-medium">
                    {selectedItems.length}{" "}
                    {__("selected", "wpbean-quantity-controls-for-woocommerce")}
                </p>
            )}
        </div>
    );
};

export default MultiSelect;
